import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import Zone from "@insite/client-framework/Components/Zone";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getQuoteState } from "@insite/client-framework/Store/Data/Quotes/QuotesSelector";
import loadQuoteIfNeeded from "@insite/client-framework/Store/Pages/RfqQuoteDetails/Handlers/LoadQuoteIfNeeded";
import setExpirationDate from "@insite/client-framework/Store/Pages/RfqQuoteDetails/Handlers/SetExpirationDate";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Page from "@insite/mobius/Page";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import themeColors, { themeFontSizes, themeFontWeights, themeIndentSizes } from "@MasterBluePrint/Styles/variables";
import React, { useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const { search } = getLocation(state);
    const parsedQuery = parseQueryString<{ quoteId?: string }>(search);
    const quoteId = parsedQuery.quoteId;
    return {
        quoteId,
        quoteState: getQuoteState(state, quoteId),
    };
};

const mapDispatchToProps = {
    loadQuoteIfNeeded,
    setExpirationDate,
};

export interface RfqQuoteDetailsPageStyles {
    loadFailedWrapper?: InjectableCss;
    loadFailedText?: TypographyPresentationProps;
    quickLinksGridItem?:GridItemProps;
    mainSectionGridItem?:GridItemProps;
}

export const rfqQuoteDetailsPageStyles: RfqQuoteDetailsPageStyles = {
    loadFailedWrapper: {
        css: css`
            display: flex;
            height: 200px;
            justify-content: center;
            align-items: center;
            background-color: ${getColor("common.accent")};
        `,
    },
    loadFailedText: { weight: "bold" },
    quickLinksGridItem: { 
        width: [12, 12, 12, 2, 2],
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            max-width: 100%;
                            flex-basis: 100%;
                        `,
                        null,
                        css`
                            background: ${themeColors.colorWhite};
                            padding-left: 0;
                            padding-right: 0;
                            box-shadow: 2px 0 4px 0 rgba(0,37,84,0.05);
                        `,
                        css`
                            max-width: 17rem;
                            flex-basis: 17rem;
                        `,
                        null,
                    ],
                    "min",
                )
            };
            @media screen and (max-width: 1279px) {
                max-width: 100% !important;
                flex-basis: 100% !important;
                background: transparent !important;
                box-shadow: none !important; 
            }
            @media screen and (min-width: 990px) and (max-width: 1199px) {
                max-width: 100%;
                flex-basis: 100%;
            }
            @media screen and (min-width: 990px) and (max-width: 1279px) {
                padding-bottom: 0;
            }
            @media print {
                display: none !important;;
            }
        `,
    },
    mainSectionGridItem: { 
        width: [12, 12, 12, 10, 10],
        css: css`
            position: relative;
            &.quote-detail-content {
                > div {
                    position: relative;

                    > div:first-child {
                        > div > div > div {
                            h1[data-test-selector="PageTitle"] {
                                @media print {
                                    margin-bottom: 1rem;
                                    
                                    &:after {
                                        display: none;
                                    }
                                }
                                @media print and (orientation: landscape){
                                    margin-bottom: 0.5rem;
                                }
                            }
                        }
                        
                        > div {
                            > div:last-child {
                                > div:first-child {
                                    max-width: 61%;                                    
                                }
                            }
                        }
                    }
                    > div:nth-child(2) {
                        > div {
                            > div:first-child {
                                > div {
                                    > div:first-child {
                                        @media print and (orientation: portrait){
                                            display: none;
                                        }
                                        @media print and (orientation: landscape){
                                            display: none;
                                        }
                                    }
                                    > div {
                                        > div {
                                            > div {
                                                > div {
                                                    > div:first-child {
                                                        border-bottom: 1px solid #C2D1D9;
                                                        padding-top: 0.9375rem;
                                                        margin-bottom: 1.25rem;

                                                        ${({ theme }: { theme: BaseTheme }) =>
                                                        breakpointMediaQueries(
                                                            theme,
                                                            [
                                                                null,
                                                                css`
                                                                    text-align: left;

                                                                    > div {
                                                                        > div[class^='GridItemStyle'], div[class*='GridItemStyle'] {
                                                                            @media print {
                                                                                padding-top: 0 !important;
                                                                            }
                                                                            &:first-child {
                                                                                padding-top: 1.25rem;
                                                                            }
                                                                            &:last-child {
                                                                                padding-bottom: 1.25rem;
                                                                            }
                                                                            > div {
                                                                                > div {
                                                                                    padding-bottom: 0;
                                                                                    @media print {
                                                                                        padding-bottom: 0.3125rem;
                                                                                    }
                                                                                    
                                                                                    h3[class^='TypographyStyle'],
                                                                                    h3[class*='TypographyStyle'],
                                                                                    span[class^='TypographyStyle'],
                                                                                    span[class*='TypographyStyle']{
                                                                                        margin-bottom: 0;
                                                                                    }
                                                                                }
                                                                            }
                                                                        }
                                                                    }
                                                                `,
                                                            ],
                                                            "max",
                                                        )}             
                                                    }
                                                    > div:nth-child(2) {
                                                        margin-bottom: 0.75rem;
                                                        > div {
                                                            > div:first-child {
                                                                > div {
                                                                    background: ${themeColors.colorWhite};
                                                                    padding: 0.9375rem 1.5625rem 1.5625rem;
                                                                }
                                                                
                                                                ${({ theme }: { theme: BaseTheme }) =>
                                                                breakpointMediaQueries(
                                                                    theme,
                                                                    [
                                                                        null,
                                                                        css`
                                                                            > div {
                                                                                padding: 0.9375rem 1.05rem 1.5625rem;
                                                                            }
                                                                        `,
                                                                    ],
                                                                    "max",
                                                                )}

                                                                ${({ theme }: { theme: BaseTheme }) =>
                                                                breakpointMediaQueries(
                                                                    theme,
                                                                    [
                                                                        null,
                                                                        null,
                                                                        null,
                                                                        css`
                                                                            margin-right: 0.9375rem;
                                                                            max-width: 43%; 
                                                                        `,
                                                                    ],
                                                                    "min",
                                                                )}                                                
                                                                                                                            
                                                            }
                                                            > div:nth-child(2) {
                                                                padding: 0.9375rem 0.2rem 0.4688rem .5625rem;
                                                                                                                              
                                                                ${({ theme }: { theme: BaseTheme }) =>
                                                                breakpointMediaQueries(
                                                                    theme,
                                                                    [
                                                                        null,
                                                                        css`
                                                                            padding: 1.25rem 0.2rem 0.4688rem 1.55rem;
                                                                            max-width: 50%;
                                                                        `,
                                                                    ],
                                                                    "max",
                                                                )}

                                                            }
                                                            > div:last-child {
                                                                padding: 0.9375rem 0.8rem 0.4688rem 1.125rem;

                                                                ${({ theme }: { theme: BaseTheme }) =>
                                                                breakpointMediaQueries(
                                                                    theme,
                                                                    [
                                                                        null,
                                                                        css`
                                                                            padding-top: 1.25rem;
                                                                            max-width: 50%;
                                                                        `,
                                                                    ],
                                                                    "max",
                                                                )}
                                                            }
                                                        }
                                                    }
                                                    > div:last-child {
                                                        padding: 0.625rem 0.625rem 0.625rem 1.5rem;
                                                        border-radius: 2px;
                                                        background: #F0F2F3;

                                                        ${({ theme }: { theme: BaseTheme }) =>
                                                        breakpointMediaQueries(
                                                            theme,
                                                            [
                                                                null,
                                                                css`
                                                                    display: none;
                                                                `,
                                                            ],
                                                            "max",
                                                        )}
                                                    }
                                                }
                                            }
                                        }
                                    }
                                }
                                @media print {
                                    width: 100%;
                                    max-width: 100%;
                                }
                            }
                            
                            > div:last-child {
                                > div {
                                    background: ${themeColors.colorWhite};
                                    padding: 1.25rem;
                                    box-shadow: 0 2px 4px 4px rgba(0,0,0,0.03);
	                                border-radius: 2px;
                                    > div {
                                        &:nth-child(2) {
                                            display:none;
                                            .quoteNotesGrid {
                                                display:none;
                                            }
                                        }
                                    }
                                }
                                ${({ theme }: { theme: BaseTheme }) =>
                                breakpointMediaQueries(
                                    theme,
                                    [
                                        null,
                                        css`
                                        > div {
                                            padding: 0;
                                            background: transparent;
                                            box-shadow: none;
                                            > div {
                                                &:first-child {
                                                    padding: 1.25rem;
                                                    background: #fff;
                                                    box-shadow: 0 2px 4px 4px rgba(0,0,0,0.03);
                                                }
                                                &:nth-child(2) {
                                                    padding: 1.25rem;
                                                    display:block;
                                                    background: #F0F2F3;
                                                    margin-bottom: 2.5rem;
                                                    .quoteNotesGrid {
                                                        display:block;
                                                    }
                                                }
                                            }
                                        }
                                        `,
                                    ],
                                    "max",
                                )}
                                @media print {
                                    width: 50%;
                                    max-width: 50%;
                                }
                            }
                        }
                    }
                }
            }
            
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            max-width: 100%;
                            flex-basis: 100%;
                        `,
                        null,
                        null,
                        css`
                            max-width: calc(100% - 17.5rem);
                            flex-basis: calc(100% - 17.5rem);
                            padding-left: 1.5625rem;
                        `,
                        null,
                    ],
                    "min",
                )
            };
            @media print {
                max-width: 100%;
                width: 100%
            }
            @media screen and (min-width: 990px) and (max-width: 1199px) {
                max-width: 100%;
                flex-basis: 100%;
            }
            @media screen and (max-width: 1279px) {
                max-width: 100% !important;
                flex-basis: 100% !important;

                .totalReorderbtnWrapper {
                    display: none;
                }
            }
        `,
    }
};

type Props = PageProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const RfqQuoteDetailsPage = ({ id, quoteId, quoteState, loadQuoteIfNeeded, setExpirationDate }: Props) => {  
    useEffect(() => {
        if (quoteId && !quoteState.isLoading && !quoteState.errorStatusCode) {
            loadQuoteIfNeeded({ quoteId });
        }
    }, [quoteId, quoteState.isLoading, quoteState.value]);

    useEffect(() => {
        setExpirationDate({ expirationDate: undefined });
    }, []);

    const styles = rfqQuoteDetailsPageStyles;

    return (
        <Page data-test-selector="rfqQuoteDetail">
            {quoteState.errorStatusCode === 404 ? (
                <StyledWrapper {...styles.loadFailedWrapper}>
                    <Typography {...styles.loadFailedText}>{siteMessage("Rfq_OrderNotFound")}</Typography>
                </StyledWrapper>
            ) : (
                
                <>
                <GridContainer>
                    <GridItem {...styles.quickLinksGridItem}>
                        <Zone contentId={id} zoneName="NavigationList" />
                    </GridItem>
                    <GridItem {...styles.mainSectionGridItem} className="quote-detail-content">
                        <Zone contentId={id} zoneName="Content" />
                    </GridItem>
                </GridContainer>
            </>
            )}
        </Page>
    );
};

const pageModule: PageModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(RfqQuoteDetailsPage),
    definition: {
        hasEditableUrlSegment: true,
        hasEditableTitle: true,
        pageType: "System",
    },
};

export default pageModule;

/**
 * @deprecated Use string literal "RfqQuoteDetailsPage" instead of this constant.
 */
export const RfqQuoteDetailsPageContext = "RfqQuoteDetailsPage";
